import { useBusinesses } from '@/hooks/useBusiness'
import { useBusinessContext } from '@/context/BusinessContext'

interface BusinessSelectorProps {
  className?: string
}

export function BusinessSelector({ className = '' }: BusinessSelectorProps) {
  const { selectedBusinessId, setSelectedBusinessId } = useBusinessContext()
  const { data, isLoading, error } = useBusinesses({ limit: 100 })

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`} data-testid="business-selector-loading">
        <div className="h-9 bg-gray-200 rounded w-48" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`text-sm text-red-600 ${className}`} data-testid="business-selector-error">
        Failed to load businesses
      </div>
    )
  }

  const businesses = data?.businesses || []

  if (businesses.length === 0) {
    return (
      <div className={`text-sm text-gray-500 ${className}`} data-testid="business-selector-empty">
        No businesses available
      </div>
    )
  }

  const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId)

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="business-selector">
      <label htmlFor="business-select" className="text-sm font-medium text-gray-700">
        Business:
      </label>
      <select
        id="business-select"
        value={selectedBusinessId || ''}
        onChange={(e) => setSelectedBusinessId(e.target.value || null)}
        className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
        data-testid="business-select"
      >
        <option value="">Select a business...</option>
        {businesses.map((business) => (
          <option key={business.id} value={business.id}>
            {business.name}
          </option>
        ))}
      </select>
      {selectedBusiness && (
        <span className="text-xs text-gray-500">
          ({selectedBusiness.accounting_identifier || 'No ID'})
        </span>
      )}
    </div>
  )
}
